/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import keysToConstants from 'js/lib/keysToConstants';

import { Dialog, Link, Typography2 } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';

import { getS12nEnrollModalCopies } from 'bundles/coursera-plus-common/components/elevateLTV/prePurchaseUpsell/PrePurchaseUpsellEnrollCopies';
import type { ApiError } from 'bundles/enroll-common/utils/errorUtils';
import { useIsLimitedFreeTrialEnabled } from 'bundles/enroll-common/utils/freeTrialOptimizationsUtils';
import { useEnrollInS12nSubscription } from 'bundles/enroll-common/utils/useEnrollInS12nSubscription';
import EnrollErrorModal from 'bundles/enroll-modal/components/common/EnrollErrorModal';
import MixAndMatchS12nContextMessage from 'bundles/enroll-modal/components/common/MixAndMatchS12nContextMessage';
import PromotionApplicableCheckoutMessage from 'bundles/enroll-modal/components/common/PromotionApplicableCheckoutMessage';
import S12nSubscriptionEnrollButton from 'bundles/enroll-modal/components/s12n-subscriptions/S12nSubscriptionEnrollButton';
import S12nSubscriptionFooter from 'bundles/enroll-modal/components/s12n-subscriptions/S12nSubscriptionFooter';
import usePageData from 'bundles/enroll-modal/data/usePageData';
import { useEnrollModalEventing } from 'bundles/enroll-modal/utils/eventingUtils';
import ReactPriceDisplay from 'bundles/ownable-products-common/components/ReactPriceDisplay';

import _t from 'i18n!nls/enroll';

const MODAL_TYPES = keysToConstants(['ENROLL', 'ERROR']);

const styles = {
  dialog: css`
    .cds-Dialog-dialog {
      max-width: 720px !important;
    }
  `,
  header: css`
    padding-bottom: var(--cds-spacing-100);
  `,
  contextMessage: css`
    margin-bottom: var(--cds-spacing-300);
  `,
  subtitleBelowContextMessage: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  subtitle: css`
    margin-bottom: var(--cds-spacing-400);
  `,
  valueProps: css`
    padding: 0;
    margin: 0;
    list-style-type: none;

    li:first-child {
      padding-top: 0;
    }
  `,
  valuePropListItem: css`
    display: flex;
    align-items: flex-start;
    padding: var(--cds-spacing-100) 0;
  `,
  valuePropHeader: css`
    margin: 0;
  `,
  valuePropSubheader: css`
    padding-top: 4px;
  `,
  checkMark: css`
    flex-shrink: 0;
    margin-right: var(--cds-spacing-100);
  `,
  actions: css`
    padding: var(--cds-spacing-300);

    /* SubscriptionEnrollButton.styl adds a 16px vertical margin meant for V1's legacy modal,
       which has no equivalent padding of its own. Dialog.Actions already provides spacing here. */
    .rc-SubscriptionEnrollButton {
      margin: 0;
    }
  `,
  promoMessage: css`
    padding-bottom: var(--cds-spacing-100);
  `,
};

type PropsFromCaller = {
  onClose: () => void;
  onSdp: boolean;
  isFromS12nSelection?: boolean;
  didSkipMixAndMatchModal?: boolean;
  // Learner is in the optimize-mix-and-match experiment and had the selection step skipped for them
  isMixAndMatchOrderingExperiment?: boolean;
  onChangeS12n?: () => void;
  isGuestCheckout?: boolean;
  onGuestCheckoutUnavailable?: () => void;
};

const S12nSubscriptionEnrollModal = ({
  onClose,
  onSdp,
  isFromS12nSelection = false,
  didSkipMixAndMatchModal = false,
  isMixAndMatchOrderingExperiment = false,
  onChangeS12n,
  isGuestCheckout,
  onGuestCheckoutUnavailable,
}: PropsFromCaller) => {
  const [activeModal, setActiveModal] = useState<keyof typeof MODAL_TYPES | null>(MODAL_TYPES.ENROLL);
  const [error, setError] = useState<ApiError | undefined>();
  const [enrolling, setEnrolling] = useState(false);
  const { getEnrollModalTrackingRef, trackEnrollModalClose, trackEnrollModalSecondaryCta } = useEnrollModalEventing();
  const visibilityTrackingRef = getEnrollModalTrackingRef();
  const { s12n, course, s12nDerivatives, s12nSubscriptionOwnableProduct, isSpecialization } = usePageData();
  const { s12nMonthlySkuId, canEnrollThroughS12nSubscriptionFreeTrial, enrollInSubscription } =
    useEnrollInS12nSubscription(isFromS12nSelection);
  const s12nSubscriptionPrice = s12nSubscriptionOwnableProduct?.price ?? s12nDerivatives?.catalogPrice;
  const shouldRender = !!s12n && !!s12nMonthlySkuId && !!s12nSubscriptionPrice;
  React.useEffect(() => {
    if (isGuestCheckout && !shouldRender) {
      onGuestCheckoutUnavailable?.();
    }
  }, [isGuestCheckout, onGuestCheckoutUnavailable, shouldRender]);
  // Also covers the null-render guard below, since that guard runs after this hook call (hooks
  // can't be conditional) and would otherwise fetch the exclusion check while required data (e.g.
  // price) is still loading and the modal hasn't rendered yet.
  const { isLimitedFreeTrialEnabled } = useIsLimitedFreeTrialEnabled({
    shouldRecordExposure: canEnrollThroughS12nSubscriptionFreeTrial && shouldRender,
  });

  if (!shouldRender) {
    return null;
  }

  const { HeadingGroup, Content, Actions } = Dialog;

  const copy = getS12nEnrollModalCopies({
    s12nSubscriptionPrice,
    s12nProductVariant: s12n.productVariant,
    s12n,
    course,
    isLimitedFreeTrialEnabled,
  });

  const handleClose = () => {
    trackEnrollModalClose();
    onClose();
  };

  const handleChangeS12n = () => {
    trackEnrollModalSecondaryCta('mix_and_match_change_program');
    onChangeS12n?.();
  };

  const handleError = (apiError: ApiError) => {
    setEnrolling(false);
    setActiveModal(MODAL_TYPES.ERROR);
    setError(apiError);
  };

  const onClickSubscribeCta = () => {
    setEnrolling(true);
    enrollInSubscription().catch(handleError);
  };

  const getTitle = () => {
    if (canEnrollThroughS12nSubscriptionFreeTrial) {
      return copy.freeTrialTitle;
    } else {
      return copy.paidTitle;
    }
  };

  const getSubtitle = () => {
    if (onSdp && canEnrollThroughS12nSubscriptionFreeTrial) {
      return null;
    } else if (onSdp && !canEnrollThroughS12nSubscriptionFreeTrial) {
      return copy.paidS12nSubtitle;
    } else if (!onSdp && canEnrollThroughS12nSubscriptionFreeTrial) {
      // When the mix-and-match modal was skipped, the specialization context is already shown
      // above, so drop the duplicate sentence and keep only the "...includes:" lead-in.
      return didSkipMixAndMatchModal ? copy.freeTrialSubtitleShort : copy.freeTrialSubtitle;
    } else {
      return didSkipMixAndMatchModal ? copy.paidCourseSubtitleShort : copy.paidCourseSubtitle;
    }
  };

  const getValueProps = () => {
    if (canEnrollThroughS12nSubscriptionFreeTrial) {
      const valueProps = copy.freeTrialValueProps.FREE_TRIAL_LOGGED_IN_BULLETS;
      return (
        <ul css={styles.valueProps}>
          {valueProps.map((valueProp) => (
            <li key={`value-prop-${valueProp.header}`} css={styles.valuePropListItem}>
              <CheckIcon size="large" color="interactive" css={styles.checkMark} />
              <div>
                <Typography2 component="div" variant="subtitleMedium" css={styles.valuePropHeader}>
                  <FormattedMessage
                    message={valueProp.header}
                    monthlyPrice={
                      <ReactPriceDisplay
                        value={s12nSubscriptionPrice.amount}
                        currency={s12nSubscriptionPrice.currencyCode}
                        hideCurrencyCode={true}
                      />
                    }
                  />
                </Typography2>
                <Typography2 component="div" variant="bodySecondary" css={styles.valuePropSubheader}>
                  {valueProp.subheader}
                </Typography2>
              </div>
            </li>
          ))}
        </ul>
      );
    } else {
      const valueProps = copy.paidValueProps;
      return (
        <ul css={styles.valueProps}>
          {valueProps.map((valueProp) => (
            <li key={`value-prop-${valueProp}`} css={styles.valuePropListItem}>
              <CheckIcon size="large" color="interactive" css={styles.checkMark} />
              <Typography2 component="p">{valueProp}</Typography2>
            </li>
          ))}
        </ul>
      );
    }
  };

  switch (activeModal) {
    case MODAL_TYPES.ENROLL: {
      const subtitle = getSubtitle();
      return (
        <Dialog
          data-testid="subscription-enroll-modal"
          open
          aria-describedby="dialog-content"
          width="medium"
          variant="standard"
          ref={visibilityTrackingRef}
          sticky="actions"
          onClose={handleClose}
        >
          <HeadingGroup css={styles.header}>{getTitle() as string}</HeadingGroup>
          <Content id="dialog-content">
            <div css={styles.promoMessage}>
              <PromotionApplicableCheckoutMessage
                course={course}
                s12n={s12n}
                isFromS12nSelection={isFromS12nSelection}
              />
            </div>
            {isMixAndMatchOrderingExperiment ? (
              <React.Fragment>
                <Typography2
                  variant="bodyPrimary"
                  component="p"
                  css={styles.contextMessage}
                  data-testid="mix-and-match-s12n-context"
                >
                  <MixAndMatchS12nContextMessage variant="experiment" />
                  {onChangeS12n && (
                    <React.Fragment>
                      {' '}
                      <Link component="button" onClick={handleChangeS12n} data-testid="mix-and-match-change-s12n">
                        {_t('Change program')}
                      </Link>
                    </React.Fragment>
                  )}
                </Typography2>
                {subtitle && (
                  <Typography2
                    variant="bodyPrimary"
                    component="div"
                    css={[styles.subtitle, styles.subtitleBelowContextMessage]}
                    data-testid="subscription-modal-subtitle"
                  >
                    {subtitle}
                  </Typography2>
                )}
              </React.Fragment>
            ) : didSkipMixAndMatchModal ? (
              <Typography2
                variant="bodyPrimary"
                component="p"
                css={styles.subtitle}
                data-testid="mix-and-match-s12n-context"
              >
                <MixAndMatchS12nContextMessage /> {subtitle}
              </Typography2>
            ) : (
              subtitle && (
                <Typography2
                  variant="bodyPrimary"
                  component="div"
                  css={styles.subtitle}
                  data-testid="subscription-modal-subtitle"
                >
                  {subtitle}
                </Typography2>
              )
            )}
            {getValueProps()}
          </Content>
          <Actions css={styles.actions}>
            <S12nSubscriptionEnrollButton
              isFromS12nSelection={isFromS12nSelection}
              showCds={true}
              onClick={onClickSubscribeCta}
              enrolling={enrolling}
            />
            {!onSdp && (
              <div>
                <S12nSubscriptionFooter />
              </div>
            )}
          </Actions>
        </Dialog>
      );
    }
    case MODAL_TYPES.ERROR:
      return (
        <EnrollErrorModal
          error={error}
          onClose={handleClose}
          isFinancialAid={false}
          isFreeEnroll={false}
          courseId={course?.id}
          s12nId={s12n?.id}
          isSpecialization={isSpecialization}
        />
      );
    default:
      return null;
  }
};

export default S12nSubscriptionEnrollModal;
